import * as React from "react";
import {useEffect, useState} from "react";
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Grid
} from "@mui/material";
import FormWrapper from "../PaperFormWrapper/PaperFormWrapper";
import {useSnackbar} from "notistack";
import {Organization} from "../../models/organization";
import {useCurrentUser, useUpdateUser} from "../../hooks/users";
import {useOrganizationChoices} from "../../hooks/organizations";

export default (props) => {
    const [loading, setLoading] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [firstName, setFirstName] = useState(props.user.first_name);
    const [lastName, setLastName] = useState(props.user.last_name);
    const [email, setEmail] = useState(props.user.email);
    const [organization, setOrganization] = useState(props.user.organization);
    const [verified, setVerified] = useState(props.user.verified);

    const {enqueueSnackbar} = useSnackbar();

    const {isSuccess, data: currentUser} = useCurrentUser();
    const {isSuccess: isOrgSuccess, data: orgList} = useOrganizationChoices();
    const updateUserMutator = useUpdateUser();

    const toastHelper = (msg, type) => {
        enqueueSnackbar(msg, {
            preventDuplicate: true,
            variant: type,
            anchorOrigin: {horizontal: 'right', vertical: 'bottom'}
        });
    }

    useEffect(() => {
        isFormValid()
    }, [firstName, lastName, email, organization, verified, props.user]);

    const submitUserInfoChange = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);

        updateUserMutator.mutate(
            {
                id: props.user.id,
                payload: {
                    first_name: firstName,
                    last_name: lastName,
                    email,
                    organization,
                    verified
                }
            },
            {
                onSuccess: () => {
                    toastHelper("User information update successfully!", "success");
                    setLoading(false);
                    if (props.submitCallback) {
                        props.submitCallback();
                    }
                },
                onError: (err) => {
                    console.error(err)
                    toastHelper("An error occurred, please try again.", "error");
                    setLoading(false);
                    if (props.submitCallback) {
                        props.submitCallback();
                    }
                }
            }
        );
    }

    const handleFirstNameChange = (event) => {
        setFirstName(event.target.value);
    }

    const handleLastNameChange = (event) => {
        setLastName(event.target.value);
    }

    const handleEmailChange = (event) => {
        setEmail(event.target.value);
    }

    const handleOrgChange = (event) => {
        setOrganization({name: event.target.value});
    }

    const handleVerifiedChange = (event) => {
        setVerified(event.target.checked);
    }

    const isFormValid = () => {
        const notNull = firstName !== "" && lastName !== "" && email !== "";
        const validFirstName = firstName !== props.user.first_name;
        const validLastName = lastName !== props.user.last_name;
        const validEmail = email !== props.user.email;
        const validOrg = organization.name !== props.user.organization.name;
        const validVerified = verified !== props.user.verified;
        setIsValid(notNull && (validFirstName || validLastName || validEmail || validOrg || validVerified));
    }

    return (
        <FormWrapper
            topPadding={0}
            title={props.title}
            onSubmit={submitUserInfoChange}
            loading={loading}
        >
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <TextField
                        size="small"
                        margin="normal"
                        required
                        fullWidth
                        name="firstName"
                        label="First Name"
                        type="text"
                        id="firstName"
                        value={firstName}
                        disabled={loading}
                        onChange={handleFirstNameChange}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextField
                        size="small"
                        margin="normal"
                        required
                        fullWidth
                        name="lastName"
                        label="Last Name"
                        type="text"
                        id="lastName"
                        value={lastName}
                        disabled={loading}
                        onChange={handleLastNameChange}
                    />
                </Grid>
            </Grid>
            <TextField
                size="small"
                margin="normal"
                required
                fullWidth
                name="email"
                label="Email"
                type="email"
                id="email"
                value={email}
                disabled={loading}
                onChange={handleEmailChange}
            />
            {
                (isSuccess && currentUser.is_staff) && (
                    <FormControl size="small" margin="normal" required fullWidth>
                        <InputLabel id="select-label">Organization</InputLabel>
                        <Select
                            labelId="select-label"
                            name="organization"
                            value={organization.name}
                            label="Organization"
                            disabled={loading}
                            onChange={handleOrgChange}
                        >
                            {
                                isOrgSuccess &&
                                orgList.map((org: Organization) => {
                                    return (<MenuItem key={org.id} value={org.name}>{org.name}</MenuItem>)
                                })
                            }
                        </Select>
                    </FormControl>
                )
            }
            {
                (isSuccess && currentUser.is_staff && currentUser.id !== props.user.id) && (
                    <FormGroup>
                        <FormControlLabel
                            label="Verified"
                            control={
                                <Checkbox name="verified"
                                          onChange={handleVerifiedChange}
                                          disabled={loading}
                                          checked={verified}/>
                            }
                        />
                    </FormGroup>
                )
            }
            <Button
                type="submit"
                variant="contained"
                sx={{mt: 3, float: 'right'}}
                disabled={loading || !isValid}
            >
                Save
            </Button>
        </FormWrapper>
    )
}